"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { listSabineChats } from "@/server-actions/sabine-chat/actions";

const CHATS_KEY = "chats";

/**
 * The Chats menu with every one of its member's chats, newest first, below
 * "All chats" (the library, with search): each a link to where the chat
 * carries on, with Sabine's pane open. "All chats" comes first so a long list
 * does not bury it. The list is read once when the nav mounts, so the menu
 * opens on it, and again whenever the menu can be seen (`visible`: its panel
 * or the phone drawer is open, or the page is a chat's), so a chat started a
 * page ago is there; the last list stays up while the next one loads. The
 * rows are marked `recent`, so no bookmark star is offered on them.
 *
 * @param {Array} menus  From `buildNavSections`.
 * @param {{ visible: boolean }} options
 */
export function useChatsMenu(menus, { visible }) {
  const [chats, setChats] = useState([]);
  const hasChatsMenu = menus.some((menu) => menu.key === CHATS_KEY);
  const read = useRef(false);

  useEffect(() => {
    if (!hasChatsMenu || (read.current && !visible)) return undefined;
    read.current = true;
    let cancelled = false;
    listSabineChats()
      .then((listed) => {
        if (!cancelled) setChats(listed);
      })
      // The action already answers [] when it cannot read the list; this is
      // the request itself failing. The last list stays up.
      .catch((error) => console.error("[useChatsMenu] Failed to list chats", error));
    return () => {
      cancelled = true;
    };
  }, [visible, hasChatsMenu]);

  return useMemo(
    () =>
      menus.map((menu) =>
        menu.key === CHATS_KEY && chats.length
          ? {
              ...menu,
              items: [
                ...menu.items,
                ...chats.map((chat) => ({
                  // Two chats can open the same page, so the row's key is
                  // the chat's id rather than its href.
                  key: chat.id,
                  href: chat.href,
                  label: chat.title,
                  iconName: chat.iconName,
                  display: true,
                  recent: true,
                })),
              ],
            }
          : menu,
      ),
    [menus, chats],
  );
}
